import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/useMediaQuery';
import type { Aggregation, AggregationValue } from '@/types/job.types';
import styles from './JobFilters.module.css';

/**
 * Filter + sort dropdowns shared by Home and Jobs.
 * Mirrors Flutter MultiSelectCheckboxDrpDwn (FilterContainer) and CustomJobListSortByFilterWidget.
 */

function useDismiss(isOpen: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isOpen, close]);
  return ref;
}

/** Aggregations without a `title` fall back to their key: 'employmentType' → 'Employment type'. */
function humanizeKey(key: string): string {
  const words = key.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

interface FilterDropdownProps {
  aggregation: Aggregation;
  /** Selection change — Flutter onClickOption / onOptionRemoved */
  onChange: (values: AggregationValue[]) => void;
  onApply: () => void;
  /** Flutter onClearFilter(key) */
  onClear: (key: string) => void;
}

/** Multi-select with search, Clear and Apply (FilterContainer → MultiSelectCheckboxDrpDwn, isApplyButtonEnabled). */
export function FilterDropdown({ aggregation, onChange, onApply, onClear }: FilterDropdownProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const close = () => {
    setIsOpen(false);
    setQuery('');
  };
  const ref = useDismiss(isOpen, close);

  const name = aggregation.name ?? '';
  const values = aggregation.values ?? [];
  const checkedCount = values.filter((v) => v.isChecked).length;
  const q = query.trim().toLowerCase();
  const visible = q
    ? values.filter((v) => (v.displayKey ?? v.key ?? '').toLowerCase().includes(q))
    : values;

  return (
    <div className={styles.dropdown} ref={ref}>
      <button
        type="button"
        className={`${styles.trigger} ${checkedCount > 0 ? styles.triggerActive : ''}`}
        onClick={() => (isOpen ? close() : setIsOpen(true))}
        aria-expanded={isOpen}
      >
        <span className={styles.triggerLabel}>
          {t(aggregation.title ?? name, { defaultValue: aggregation.title ?? humanizeKey(name) })}
        </span>
        {checkedCount > 0 && <span className={styles.badge}>{checkedCount}</span>}
        <Chevron />
      </button>

      {isOpen && <div className={styles.backdrop} onClick={close} />}
      {isOpen && (
        <div className={styles.panel}>
          <input
            className={styles.search}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('Search')}
            // On mobile the keyboard would cover the bottom sheet
            autoFocus={!isMobile}
          />
          <div className={styles.options} role="listbox" aria-multiselectable="true">
            {visible.map((val) => (
              <label key={val.key ?? val.displayKey} className={styles.option}>
                <input
                  type="checkbox"
                  checked={!!val.isChecked}
                  onChange={() =>
                    onChange([
                      {
                        catgKey: name,
                        key: val.key,
                        displayKey: val.displayKey,
                        additionalFields: val.additionalFields,
                        isChecked: !val.isChecked,
                      },
                    ])
                  }
                />
                <span className={styles.optionLabel}>{t(val.displayKey ?? val.key ?? '')}</span>
                {val.count != null && <span className={styles.count}>{val.count}</span>}
              </label>
            ))}
            {visible.length === 0 && <p className={styles.empty}>{t('No records found')}</p>}
          </div>
          <div className={styles.footer}>
            <button
              type="button"
              className={styles.clearBtn}
              onClick={() => {
                onClear(name);
              }}
            >
              {t('Clear')}
            </button>
            <button
              type="button"
              className={styles.applyBtn}
              onClick={() => {
                close();
                onApply();
              }}
            >
              {t('Apply')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface SortDropdownProps {
  aggregation: Aggregation;
  /** Called with the Flutter sort selection value; the caller applies it immediately. */
  onSelect: (value: AggregationValue) => void;
}

/** Single-select sort with ascending / descending buttons (Flutter _buildCustomSortOption). */
export function SortDropdown({ aggregation, onSelect }: SortDropdownProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useDismiss(isOpen, () => setIsOpen(false));
  const values = aggregation.values ?? [];
  const selected = values.find((v) => v.isChecked);

  const choose = (val: AggregationValue, order: 'asc' | 'desc') => {
    setIsOpen(false);
    onSelect({
      catgKey: 'sortBy',
      key: val.key,
      displayKey: 'sortBy',
      isChecked: true,
      additionalFields: {
        key: val.key ?? '',
        displayKey: val.displayKey ?? '',
        sortBy: order,
      },
    });
  };

  return (
    <div className={styles.sortRow}>
      <span className={styles.sortLabel}>{t('Sort By:')}</span>
      <div className={styles.dropdown} ref={ref}>
        <button
          type="button"
          className={styles.trigger}
          onClick={() => setIsOpen((v) => !v)}
          aria-expanded={isOpen}
        >
          <span className={styles.triggerLabel}>
            {selected ? t(selected.displayKey ?? selected.key ?? '') : t(aggregation.title ?? 'Sort By')}
          </span>
          {selected?.sortBy && <span aria-hidden="true">{selected.sortBy === 'asc' ? '↑' : '↓'}</span>}
          <Chevron />
        </button>

        {isOpen && <div className={styles.backdrop} onClick={() => setIsOpen(false)} />}
        {isOpen && (
          <div className={styles.panel} role="listbox">
            <div className={styles.options}>
              {values.map((val) => (
                <div
                  key={val.key ?? val.displayKey}
                  className={`${styles.sortOption} ${val.isChecked ? styles.sortOptionActive : ''}`}
                >
                  <span className={styles.optionLabel}>{t(val.displayKey ?? val.key ?? '')}</span>
                  <button
                    type="button"
                    className={`${styles.orderBtn} ${val.isChecked && val.sortBy === 'asc' ? styles.orderBtnActive : ''}`}
                    onClick={() => choose(val, 'asc')}
                    aria-label={`${t(val.displayKey ?? '')} ${t('ascending')}`}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className={`${styles.orderBtn} ${val.isChecked && val.sortBy === 'desc' ? styles.orderBtnActive : ''}`}
                    onClick={() => choose(val, 'desc')}
                    aria-label={`${t(val.displayKey ?? '')} ${t('descending')}`}
                  >
                    ↓
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Chevron() {
  return (
    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
      <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
